import type { ZoomContactCenterIntegrationRequest } from "@repo/app-types/entities/integration";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { InfoBanner } from "@repo/ui/InfoBanner/infoBanner";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { SimpleModalVariant } from "@repo/ui/Modal/modal";
import { useEffect, useState, type FC } from "react";
import {
  useConnectZoomContactCenterIntegrationMutation,
  usePreviewZoomContactCenterQueuesMutation,
} from "../../../api/entities/zoomContactCenterIntegration.js";
import { getApiErrorMessage } from "../../../api/helpers/errors.js";
import { ZoomConnectionForm } from "./zoomContactCenterConnectionForm.js";
import { ZoomContactCenterQueuePicker } from "./zoomContactCenterQueuePicker.js";

interface ZoomConnectQueuesStepProps {
  credentials: ZoomContactCenterIntegrationRequest;
  onBack: () => void;
}

/**
 * Connecting's second step: Zoom's queues, listed with the credentials just
 * tested, to choose from before the connection is saved and the first poll
 * runs. When Zoom won't list them (the app has no queue scope), every queue
 * can still be imported.
 */
const ZoomConnectQueuesStep: FC<ZoomConnectQueuesStepProps> = (props) => {
  const { credentials, onBack } = props;
  // The hook's state follows the latest request only, so an earlier one that
  // answers late (StrictMode's double mount) can't replace what is shown.
  const [previewQueues, preview] = usePreviewZoomContactCenterQueuesMutation();
  const [connectIntegration, connectState] =
    useConnectZoomContactCenterIntegrationMutation();
  const [queueIds, setQueueIds] = useState<readonly string[] | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);
  const isPreviewLoading = preview.isUninitialized || preview.isLoading;

  useEffect(() => {
    void previewQueues(credentials);
    // Listed once per visit to this step; "Try again" reloads.
    // oxlint-disable-next-line react/exhaustive-deps
  }, []);

  const reloadQueues = () => {
    // Drop any selection made from an earlier list: it may name queues the
    // new one doesn't.
    setQueueIds(null);
    void previewQueues(credentials);
  };

  // Success replaces this flow with the connected Settings tab.
  const connect = async () => {
    setConnectError(null);
    try {
      await connectIntegration({
        ...credentials,
        zoom_queue_ids: queueIds ? [...queueIds] : null,
      }).unwrap();
    } catch (error) {
      setConnectError(
        getApiErrorMessage(
          error,
          "Couldn't connect Zoom Contact Center. Try again.",
        ),
      );
    }
  };

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-sm font-semibold text-foreground">Queues</h2>
        <p className="mt-1 max-w-2xl text-sm leading-5 text-muted-foreground">
          Choose which Zoom Contact Center queues to import call recordings
          from. The sync starts when you connect and imports calls from then on,
          plus any that ended in the few minutes before; you can change the
          queues later in Settings.
        </p>

        {isPreviewLoading ? (
          <div className="mt-4">
            <Loader style={LoaderStyle.SPINNER} />
          </div>
        ) : null}

        {preview.isError ? (
          <div className="mt-4 max-w-2xl space-y-3">
            <InfoBanner
              description={getApiErrorMessage(
                preview.error,
                "Zoom didn't return the queue list.",
              )}
              title="Couldn't load queues from Zoom"
              variant="CAUTION"
            />
            <p className="text-sm text-muted-foreground">
              You can still connect and import from every queue, and choose
              queues later in Settings.
            </p>
            <Button
              onClick={reloadQueues}
              size={Size.SMALL}
              variant={ButtonVariant.Secondary}
            >
              Try again
            </Button>
          </div>
        ) : null}

        {preview.data ? (
          <ZoomContactCenterQueuePicker
            isDisabled={connectState.isLoading}
            isTruncated={preview.data.is_truncated}
            minimumSelectionHint="At least one queue must stay selected."
            onChange={setQueueIds}
            queueIds={queueIds}
            zoomQueues={preview.data.queues}
          />
        ) : null}
      </section>

      {connectError ? (
        <div className="max-w-2xl">
          <InfoBanner
            description={connectError}
            title="Couldn't connect"
            variant={SimpleModalVariant.Warning}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-light pt-5">
        <Button
          isDisabled={connectState.isLoading}
          onClick={onBack}
          variant={ButtonVariant.Tertiary}
        >
          Back
        </Button>
        <Button
          isDisabled={isPreviewLoading}
          isLoading={connectState.isLoading}
          onClick={() => void connect()}
          variant={ButtonVariant.Primary}
        >
          Connect and start syncing
        </Button>
      </div>
    </div>
  );
};

/**
 * Connecting Zoom Contact Center from the detail page: credentials (tested
 * before they can be used), then the queues to import from. Nothing is stored
 * until the last step, which saves both and starts the poll, so the first
 * poll already imports only the chosen queues.
 */
export const ZoomContactCenterConnectFlow: FC = () => {
  const [credentials, setCredentials] =
    useState<ZoomContactCenterIntegrationRequest | null>(null);
  const [step, setStep] = useState<"credentials" | "queues">("credentials");

  if (step === "queues" && credentials) {
    return (
      <ZoomConnectQueuesStep
        credentials={credentials}
        onBack={() => setStep("credentials")}
      />
    );
  }

  return (
    <ZoomConnectionForm
      initialCredentials={credentials ?? undefined}
      onSubmit={(tested) => {
        setCredentials(tested);
        setStep("queues");
      }}
      variant="connectStep"
    />
  );
};
